import { useGetReactSelectStyles } from "@/theme/components-style-overrides/reactselect-definition"
import { Flex, Link } from "@chakra-ui/react"
import { useEffect, useMemo, useRef, useState } from "react"
import type { ControllerFieldState } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"
import ReactSelect, { type InputProps, components } from "react-select"
import {
  ADDRESS_SEARCH_MIN_CHARS,
  type GeocodedAddress,
  OSM_COPYRIGHT_URL,
  searchAddresses,
} from "../../helpers/photon"

const SEARCH_DEBOUNCE_MS = 300

type AddressOption = { value: GeocodedAddress; label: string; id: string }

// react-select hides its input after a selection (to show the selected value instead). Here the
// input is the value: keep it visible.
const AlwaysVisibleInput = (props: InputProps<AddressOption, false>) => (
  <components.Input {...props} isHidden={false} />
)

type Props = {
  value: string
  // Free text typed in the field (the address line, even when no suggestion is picked).
  onChange: (address: string) => void
  // A suggestion was picked: structured address and coordinates.
  onSelect: (address: GeocodedAddress) => void
  fieldState?: ControllerFieldState
}

// Address field of the InterBU flow: free text with Photon (OpenStreetMap) suggestions (ADR 0004 D2).
export const AddressAutocomplete: React.FC<Props> = ({ value, onChange, onSelect, fieldState }) => {
  const intl = useIntl()
  const [options, setOptions] = useState<GeocodedAddress[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)
  const skipNextSearch = useRef(false)

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false
      return
    }
    const query = value.trim()
    if (query.length < ADDRESS_SEARCH_MIN_CHARS) {
      setOptions([])
      setIsLoading(false)
      return
    }
    const controller = new AbortController()
    // Previous suggestions no longer match: show the loading message until the new ones arrive.
    setOptions([])
    setIsLoading(true)
    const timeout = setTimeout(() => {
      searchAddresses(query, intl.locale, controller.signal)
        .then(results => {
          setOptions(results)
          setHasFailed(false)
        })
        .catch(() => {
          if (controller.signal.aborted) return
          setOptions([])
          setHasFailed(true)
        })
        .finally(() => {
          if (!controller.signal.aborted) setIsLoading(false)
        })
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      clearTimeout(timeout)
      controller.abort()
    }
  }, [value, intl.locale])

  const selectOptions = useMemo<AddressOption[]>(
    () =>
      options.map((address, index) => ({
        value: address,
        label: address.label,
        id: String(index),
      })),
    [options]
  )

  const noOptionsMessageId = useMemo(() => {
    if (value.trim().length < ADDRESS_SEARCH_MIN_CHARS)
      return "userSharedData.interbu.modal.steps.1.localization.address.minChars"
    if (hasFailed) return "userSharedData.interbu.modal.steps.1.localization.address.unavailable"
    return "userSharedData.interbu.modal.steps.1.localization.address.noResult"
  }, [value, hasFailed])

  return (
    <Flex direction="column" gap="1" width="100%">
      <ReactSelect<AddressOption, false>
        inputValue={value}
        value={null}
        options={selectOptions}
        filterOption={null}
        getOptionValue={option => option.id}
        controlShouldRenderValue={false}
        isClearable={false}
        placeholder={intl.formatMessage({
          id: "userSharedData.interbu.modal.steps.1.localization.address.placeholder",
        })}
        onInputChange={(text, { action }) => {
          // Keep the typed text when the field loses focus or the menu closes.
          if (action === "input-change") onChange(text)
        }}
        onChange={option => {
          if (option === null) return
          skipNextSearch.current = true
          onChange(option.value.address)
          onSelect(option.value)
        }}
        isLoading={isLoading}
        loadingMessage={() => intl.formatMessage({ id: "loading.generic.title" })}
        noOptionsMessage={() => intl.formatMessage({ id: noOptionsMessageId })}
        styles={useGetReactSelectStyles(fieldState, { width: "100%" })}
        components={{
          DropdownIndicator: null,
          IndicatorSeparator: null,
          Input: AlwaysVisibleInput,
        }}
      />
      <Link href={OSM_COPYRIGHT_URL} isExternal fontSize="3xs" color="textSubtle" alignSelf="end">
        <FormattedMessage id="userSharedData.interbu.modal.steps.1.localization.address.attribution" />
      </Link>
    </Flex>
  )
}
